import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { StripeSubscriptionEventAdminFilterKey, StripeSubscriptionEventDto } from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { Observable } from 'rxjs';

export type StripeSubscriptionEventColumnKey =
  'eventId' | 'type' | 'processed' | 'processedAt' | 'error' | 'createdAt';

@Directive()
export abstract class AbstractStripeSubscriptionEventsDataTableCoreComponent<
  T extends StripeSubscriptionEventDto,
>
  extends BaseDataTableComponent<T, StripeSubscriptionEventColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<StripeSubscriptionEventColumnKey>[]>([
    { key: 'eventId', label: 'ID Événement', display: true },
    { key: 'type', label: 'Type', display: true },
    { key: 'processed', label: 'Traité', display: true },
    { key: 'processedAt', label: 'Traité le', display: true },
    { key: 'error', label: 'Erreur', display: false },
    { key: 'createdAt', label: 'Créé le', display: true },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (event: StripeSubscriptionEventDto) => this.navigateToEvent(event),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<StripeSubscriptionEventAdminFilterKey>[]>([
    {
      label: 'Traité',
      column: StripeSubscriptionEventAdminFilterKey.Processed,
    },
    {
      label: 'Type',
      column: StripeSubscriptionEventAdminFilterKey.Type,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const processed = params.getAll(StripeSubscriptionEventAdminFilterKey.Processed);
      const type = params.getAll(StripeSubscriptionEventAdminFilterKey.Type);
      this.filterChanged({
        changed: true,
        filter: {
          processed: processed.length ? processed : [],
          type: type.length ? type : [],
        },
      });
    });
  }

  navigateToEvent(event: StripeSubscriptionEventDto): void {
    void this.router.navigate(['/stripe-subscription-events', event._id]);
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;
  override getDisplayForEntry(key: StripeSubscriptionEventAdminFilterKey, rawValue: string): string {
    switch (key) {
      case StripeSubscriptionEventAdminFilterKey.Processed:
        return rawValue === 'true' ? 'Oui' : 'Non';
      case StripeSubscriptionEventAdminFilterKey.Type:
        return rawValue;
      default:
        return key;
    }
  }
}
